/* Repository path: frontend/src/features/estimates/ResultPage.tsx */

import type { EstimatePreview } from "../../types";
import type { PlanItem, ProfileEditResult } from "../../types";
import { Button } from "../../components/Button";
import { ErrorSummary } from "../../components/ErrorSummary";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { SupportPrompt } from "../../components/SupportPrompt";
import { EstimateView } from "./EstimateView";
import { ProfileChangeSummary } from "./ProfileChangeSummary";
import { ItemDetailNotFound, PlanItemDetail } from "./PlanItemDetail";
import { isValidEstimatePayload, planningBlockIfValid } from "../../api/estimate";
import { EmailCapture } from "../plans/EmailCapture";

interface Props {
  preview: EstimatePreview | null;
  emailPending: boolean;
  busy: boolean;
  online: boolean;
  errors: Record<string, string>;
  onEdit: () => void;
  onStart: () => void;
  onEmail: (email: string, optIn: boolean) => void;
  onPrivacy: () => void;
  onImprove: (item?: PlanItem) => void;
  /**
   * W4-05: present only when the URL fragment names an item — see
   * `../../app/router.ts` and
   * docs/architecture/adr/ADR-0009-client-route-privacy-and-item-detail-addressing.md.
   * A preview never gets a deadline control, so this sub-view is reached
   * with no `onSaveDeadline`/`onRemoveDeadline` at all — the same gate
   * `EstimateView`/`PlanItemCard` already use.
   */
  detailItemCode?: string;
  /** The item code whose detail was just closed, if any — focuses that
   * item's card once the plan reappears. See `useRouter`. */
  focusItemCode?: string;
  onFocusHandled?: () => void;
  /**
   * W4-07: the just-completed profile edit's "what changed" summary, if
   * any — lifted into `App.tsx` state and handed down here exactly the
   * way `focusItemCode`/`onFocusHandled` already flow for W4-05's
   * return-and-focus contract. `null` renders nothing.
   */
  changeSummary?: ProfileEditResult | null;
  onChangeSummaryHandled?: () => void;
  /**
   * W4-08: the shared live-region announcer, threaded down for the
   * feedback panel's own "Sending feedback"/"Feedback sent" announcements
   * — the same announcer `SavedPlanPage` already receives and passes to
   * its own W4-06/W4-07 controls. See
   * docs/architecture/features/W4-08.md §4.
   */
  announce: (message: string) => void;
}

export function ResultPage({
  preview,
  emailPending,
  busy,
  online,
  errors,
  onEdit,
  onStart,
  onEmail,
  onPrivacy,
  onImprove,
  detailItemCode,
  focusItemCode,
  onFocusHandled,
  changeSummary = null,
  onChangeSummaryHandled,
  announce,
}: Props) {
  if (!preview) {
    return (
      <section className="empty-state page--narrow">
        <p className="eyebrow">No active questionnaire</p>
        <h1>Start the check to see an estimate</h1>
        <p>Your overview could not be restored in this tab. Start a new check to generate it again.</p>
        <Button onClick={onStart}>Start the check</Button>
      </section>
    );
  }

  // Guarded the same way `EstimateView` guards itself: a structurally
  // broken payload must always fall through to its boundary card, never
  // be searched for an item here. A stale or irrelevant `detailItemCode`
  // on an invalid payload is simply ignored — there is no data to look an
  // item up in.
  if (detailItemCode && isValidEstimatePayload(preview)) {
    const item = preview.items.find((row) => row.code === detailItemCode);
    if (!item) {
      return (
        <div className="result-page">
          <ItemDetailNotFound backHref="/result" />
        </div>
      );
    }
    return (
      <div className="result-page">
        <PlanItemDetail
          key={item.code}
          item={item}
          planning={planningBlockIfValid(preview)}
          onImprove={onImprove}
          backHref="/result"
          feedback={
            <FeedbackPanel
              surface="item_detail"
              accessContext="anonymous"
              item={item}
              online={online}
              announce={announce}
            />
          }
        />
      </div>
    );
  }

  return (
    <div className="result-page">
      <ErrorSummary errors={errors} />
      <EstimateView
        estimate={preview}
        onEdit={onEdit}
        onImprove={onImprove}
        focusItemCode={focusItemCode}
        onFocusHandled={onFocusHandled}
        changeSummary={
          changeSummary && (
            <ProfileChangeSummary
              result={changeSummary}
              items={preview.items}
              onImprove={() => onImprove()}
              onDismiss={onChangeSummaryHandled}
            />
          )
        }
        savePrompt={!emailPending && (
          <EmailCapture
            idPrefix="preview"
            placement="top"
            busy={busy}
            online={online}
            onSubmit={onEmail}
            onPrivacy={onPrivacy}
          />
        )}
        feedback={
          <FeedbackPanel
            surface="result_preview"
            accessContext="anonymous"
            online={online}
            announce={announce}
          />
        }
      />
      {emailPending ? (
        <>
          <section className="confirmation confirmation--success" role="status">
            <p className="eyebrow">Secure link requested</p>
            <h2>Check your email</h2>
            <p>If that address can receive email, we’ll send a secure link shortly. Check your inbox and spam folder.</p>
          </section>
          {/*
            W4-09: shown only once value has been delivered — here, the
            moment the secure link has been requested. Renders nothing at
            all until the server resolves an enabled, allowlisted link, and
            nothing forever if it never does. See
            docs/architecture/features/W4-09.md and
            docs/architecture/adr/ADR-0013-external-support-link-boundary.md.
          */}
          <SupportPrompt placement="plan_sent" announce={announce} />
        </>
      ) : <EmailCapture idPrefix="bottom" placement="bottom" busy={busy} online={online} onSubmit={onEmail} onPrivacy={onPrivacy} />}
    </div>
  );
}
